import { useState, type FormEvent } from 'react'

import { useAuth } from '../../context/AuthContext'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { PasswordRevealButton } from '../ui/PasswordRevealButton'

type Props = {
  onSuccess?: () => void
}

export function MustChangePasswordForm({ onSuccess }: Props) {
  const { user, completePasswordChange, loading, error, clearError } = useAuth()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [formError, setFormError] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const displayName = user?.name || user?.email || 'there'

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    clearError()
    setFormError('')
    if (newPassword.length < 8) {
      setFormError('New password must be at least 8 characters.')
      return
    }
    if (newPassword !== confirmPassword) {
      setFormError('New password and confirmation do not match.')
      return
    }
    try {
      await completePasswordChange({ currentPassword, newPassword })
      onSuccess?.()
    } catch {
      /* error handled by context */
    }
  }

  const header = (
    <div className="mb-6 text-center">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-primary">Account setup</p>
      <h1 className="text-xl font-semibold text-foreground">Welcome — set your password</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Hi <span className="font-medium text-foreground">{displayName}</span>. For security, replace the
        temporary password you used to sign in with one only you know. After this, you will have full
        access to your store dashboard.
      </p>
      <p className="mt-2 text-xs text-muted-foreground">Use at least 8 characters for your new password.</p>
    </div>
  )

  return (
    <form onSubmit={handleSubmit} className="space-y-4 text-foreground" aria-busy={loading}>
      {header}
      <Input
        id="gate-currentPassword"
        type={showCurrent ? 'text' : 'password'}
        label="Current password"
        autoComplete="current-password"
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
        required
        endAdornment={
          <PasswordRevealButton
            visible={showCurrent}
            onToggle={() => setShowCurrent((v) => !v)}
            labelShow="Show current password"
            labelHide="Hide current password"
          />
        }
      />
      <Input
        id="gate-newPassword"
        type={showNew ? 'text' : 'password'}
        label="New password"
        autoComplete="new-password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        required
        minLength={8}
        endAdornment={
          <PasswordRevealButton
            visible={showNew}
            onToggle={() => setShowNew((v) => !v)}
            labelShow="Show new password"
            labelHide="Hide new password"
          />
        }
      />
      <Input
        id="gate-confirmPassword"
        type={showConfirm ? 'text' : 'password'}
        label="Confirm new password"
        autoComplete="new-password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        required
        minLength={8}
        endAdornment={
          <PasswordRevealButton
            visible={showConfirm}
            onToggle={() => setShowConfirm((v) => !v)}
            labelShow="Show confirm password"
            labelHide="Hide confirm password"
          />
        }
      />
      {(formError || error) ? (
        <p
          className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
          aria-live="polite"
        >
          {formError || error}
        </p>
      ) : null}
      <Button type="submit" className="w-full" disabled={loading} aria-disabled={loading}>
        {loading ? 'Updating…' : 'Update password and continue'}
      </Button>
    </form>
  )
}
